import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { isUsersOnlyAdmin } from "../config/roles";
import { resolveProduct, type ProductLine } from "../config/products";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "adminProduct";

const readSavedProduct = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const saveProduct = (product: ProductLine): void => {
  try {
    localStorage.setItem(STORAGE_KEY, product);
  } catch (error) {
    console.warn("Could not remember the selected product:", error);
  }
};

interface ProductContextType {
  product: ProductLine;
  /** True when this admin cannot switch products. */
  isLocked: boolean;
  switchProduct: (product: ProductLine) => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userEmail } = useAuth();
  const isLocked = isUsersOnlyAdmin(userEmail);
  const [saved, setSaved] = useState<string | null>(readSavedProduct);
  const product = resolveProduct(saved, isLocked);

  const switchProduct = useCallback((next: ProductLine) => {
    setSaved(next);
    saveProduct(next);
  }, []);

  const value = useMemo(
    () => ({ product, isLocked, switchProduct }),
    [product, isLocked, switchProduct]
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error("useProduct must be used within a ProductProvider");
  }
  return context;
};
